<template>
    <v-container class="tiering" :class="'layout-' + layout" fluid :id="baseId">
        <!-- Layout: Custom -->
        <template v-if="layout==='custom'">
            <v-layout class="tiering-content" :id="`${baseId}-tiering-custom-layout`">
                <v-flex
                    v-for="(tier, index) in tiers"
                    :key="index"
                    :style="{ width: tierWidth }" xs12 md10 offset-md1 lg10 offset-lg1
                    :id="`${baseId}-tiering-custom-layout-tier-${index}-flex`"
                >
                    <v-card
                        class="tier-summary"
                        :class="['tier-' + (index + 1)]"
                        :id="`${baseId}-tiering-custom-layout-tier-${index}-card`"
                    >
                        <v-card-title class="tier-title justify-center ma-0 pa-0" :id="`${baseId}-tiering-custom-layout-tier-${index}-card-title`">
                            <h1 class="text" :id="`${baseId}-tiering-custom-layout-tier-${index}-tier-id`">
                                {{ $nsLanguage.getTranslation('tieringDashboardPage.' + tier.tierId) }}
                            </h1>
                        </v-card-title>
                        <v-card-text class="custom-message tx-gray-dark justify-center mt-8 pa-0" :id="`${baseId}-tiering-custom-layout-tier-${index}-card-text`">
                            <div
                                class="text-center tx-em-bold-700"
                                v-html="$nsLanguage.getTranslation('tieringDashboardPage.' + tier.content)"
                                :id="`${baseId}-tiering-custom-layout-tier-${index}-tier-content`"
                            >
                            </div>
                        </v-card-text>
                    </v-card>
                </v-flex>
            </v-layout>
        </template>
        <!-- Layout: Other -->
        <template v-else>
            <v-layout :id="`${baseId}-tiering-other-layout`">
                <v-flex xs12 :id="`${baseId}-tiering-other-layout-flex`">
                    <v-tabs
                        active-class="tab-visible"
                        class="tiering-menu"
                        :class="['tiering-menu-color-' + getTierHighlightColor(visibleTierIndex)]"
                        center-active
                        centered
                        height="80px"
                        show-arrows
                        slider-size="2px"
                        v-model="visibleTierIndex"
                        :id="`${baseId}-tiering-other-layout-tabs`"
                    >
                        <v-tabs-slider color="#000000" :id="`${baseId}-tiering-other-layout-tabs-slider`"></v-tabs-slider>
                        <v-tab
                            class="tier-tab" :class="{ 'tab-active': isTierActive(index) }"
                            v-for="(tier, index) in tiers"
                            :key="index"
                            @click="switchTab('loyaltyTier' + (index + 1))"
                            :id="`${baseId}-tiering-other-layout-tabs-tab-${index}`"
                        >
                            <div class="tier-tab-container" :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-tab-container`">
                                <div v-if="!isTierLocked(index) && !isTierActive(index)" :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-inactive-image-container`">
                                    <img
                                        class="tab-image"
                                        src="~@/assets/icons/Icon_LockOpen_InactiveGray.svg"
                                        :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-inactive-image`"
                                    />
                                </div>
                                <div v-if="!isTierLocked(index) && isTierActive(index)" :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-active-image-container`">
                                    <img
                                        class="tab-image"
                                        src="~@/assets/icons/Icon_LockOpen_ActiveBlue.svg"
                                        :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-active-image`"
                                    />
                                </div>
                                <div v-if="isTierLocked(index)" :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-locked-image-container`">
                                    <img
                                        class="tab-image"
                                        src="~@/assets/icons/Icon_LockClosed_Gray.svg"
                                        :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-locked-image`"
                                    />
                                </div>
                                <div class="tab-text tx-font-primary" :id="`${baseId}-tiering-other-layout-tabs-tab-${index}-tier-id`">
                                    {{ $nsLanguage.getTranslation('tieringDashboardPage.' + tier.tierId) }}
                                </div>
                            </div>
                        </v-tab>
                    </v-tabs>
                </v-flex>
            </v-layout>
            <v-layout class="tiering-content" :id="`${baseId}-tiering-other-content`">
                <v-flex v-for="(tier, index) in tiers" :key="index" :style="{ width: tierWidth }" :id="`${baseId}-tiering-other-content-tier-${index}`">
                    <v-card
                        class="tier-summary"
                        :class="[
                            'tier-' + (index + 1),
                            { 'tier-active': isTierActive(index), 'tier-visible': isTierVisible(index) },
                            'tier-em-' + getHighlightType(index), 'tier-layer-' + getLayerIndex(index)
                        ]"
                        :id="`${baseId}-tiering-other-content-tier-${index}-card`"
                    >
                        <div class="tier-card" :id="`${baseId}-tiering-other-content-tier-${index}-card-watermark-card`">
                            <!-- Watermark -->
                            <div
                                class="summary-section summary-watermark tx-font-primary"
                                v-if="!isMobile && !isTablet && (showTierUnlockContent(tier, index) ? !tier.toggleLockWatermark : true)"
                                :id="`${baseId}-tiering-other-content-tier-${index}-card-watermark-toggle`"
                            >
                                <div class="watermark" :id="`${baseId}-tiering-other-content-tier-${index}-card-watermark-container`">
                                    <v-img
                                        :src="require( '@/assets/Watermark_Lock@2x.png' )"
                                        contain
                                        alt=""
                                        v-if="showLocked && isTierLocked(index)"
                                        :id="`${baseId}-tiering-other-content-tier-${index}-card-locked-watermark`"
                                    />
                                    <v-img
                                        :src="tier.watermark"
                                        contain
                                        alt=""
                                        v-if="!showLocked || !isTierLocked(index)"
                                        :id="`${baseId}-tiering-other-content-tier-${index}-card-watermark`"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="tier-container" :id="`${baseId}-tiering-other-content-tier-${index}-container`">
                            <v-card-title class="tier-title justify-center ma-0 pa-0" :id="`${baseId}-tiering-other-content-tier-${index}-card-title`">
                                <h1 class="text" :id="`${baseId}-tiering-other-content-tier-${index}-tier-id`">
                                    {{ $nsLanguage.getTranslation('tieringDashboardPage.' + tier.tierId) }}
                                    <span v-if="showLocked && isTierLocked(index)" :id="`${baseId}-tiering-other-content-tier-${index}-tier-id-locked`">
                                         - {{ $nsLanguage.getTranslation('tieringDashboardPage.locked') }}
                                    </span>
                                </h1>
                            </v-card-title>
                            <v-card-text
                                class="summary-number tx-font-primary justify-center ma-0 mt-4 pa-0"
                                :id="`${baseId}-tiering-other-content-tier-${index}-purchase-ratio-container`"
                            >
                                <div class="text-center" :id="`${baseId}-tiering-other-content-tier-${index}-purchase-ratio`">{{ tier.purchaseRatio | numeral('0%') }}</div>
                            </v-card-text>
                            <v-card-text
                                class="summary-message tx-font-primary tx-gray-dark justify-center mt-1 pa-0"
                                :id="`${baseId}-tiering-other-content-tier-${index}-heading-text-container`"
                            >
                                <div
                                    class="text-center tx-em-bold-700"
                                    v-html="$nsLanguage.getTranslation('tieringDashboardPage.headingText', { currency_amount: currency(tier.minSpend) })"
                                    :id="`${baseId}-tiering-other-content-tier-${index}-heading-text`">
                                </div>
                            </v-card-text>
                            <template v-if="tier.bullets && tier.bullets.length > 0">
                                <v-divider class="summary-divider hightlight mt-5" :id="`${baseId}-tiering-other-content-tier-${index}-bullets-divider`" />
                                <div class="summary-section mt-6 tx-font-primary" :id="`${baseId}-tiering-other-content-tier-${index}-bullets-container`">
                                    <ul class="summary-bullet-list" :id="`${baseId}-tiering-other-content-tier-${index}-bullets-list`">
                                        <li class="checkmark" v-for="(bullet, ji) in tier.bullets" :key="ji" :id="`${baseId}-tiering-other-content-tier-${index}-bullets-${ji}`">
                                            <v-card-text class="ma-0 pa-0" :id="`${baseId}-tiering-other-content-tier-${index}-bullets-${ji}-card`">
                                                <span
                                                    class="text tx-font-primary tx-em-bold-700 tx-gray-dark"
                                                    v-html="bullet"
                                                    :id="`${baseId}-tiering-other-content-tier-${index}-bullets-${ji}-bullet`"
                                                >
                                                </span>
                                            </v-card-text>
                                        </li>
                                    </ul>
                                </div>
                            </template>
                            <template v-if="showTierUnlockContent(tier, index)">
                                <div class="summary-section summary-unlock tx-font-primary" :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-container`">
                                    <v-divider class="summary-divider mt-6" :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-divider`"/>
                                    <div class="unlock-content mt-7" :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-title-container`">
                                        <h2
                                            class="summary-section-heading tx-font-primary text-center tx-gray-dark mb-2"
                                            :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-title`"
                                        >
                                            {{ $nsLanguage.getTranslation('tieringDashboardPage.' + tier.unlockTitle, { name: getTierName(index + 1) }) }}
                                        </h2>
                                        <!-- Unlock Template: Common -->
                                        <template v-if="tier.template === 'common' && tier.unlockText">
                                            <p
                                                class="summary-instruction tx-font-primary text-center tx-gray-light"
                                                v-if="tier.unlockLink"
                                                :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-link-container`"
                                            >
                                                <a
                                                    class="summary-link"
                                                    :href="mapLink(tier.unlockLink)"
                                                    @click="publishEvent($event, tier)"
                                                    :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-link-link`"
                                                >
                                                    {{ $nsLanguage.getTranslation('tieringDashboardPage.' + tier.unlockText) }}
                                                </a>
                                            </p>
                                            <p
                                                class="summary-instruction tx-font-primary text-center tx-gray-light"
                                                v-else
                                                :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-text`"
                                            >
                                                {{ $nsLanguage.getTranslation('tieringDashboardPage.' + tier.unlockText) }}
                                            </p>
                                        </template>
                                        <!-- Unlock Template: Progress -->
                                        <template v-if="tier.template === 'spending_progress'">
                                            <p
                                                class="summary-instruction tx-font-primary text-center tx-em-bold-700 tx-gray-light mb-4"
                                                v-html="$nsLanguage.getTranslation('tieringDashboardPage.unlockProgressDetailSpend',
                                                    { amount: currency(getNextTierThreshold(index)), period: getNextTierSpendWithinPeriod(index) }
                                                )"
                                                :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-progress-detail-spend`"
                                            >
                                            </p>
                                            <template v-if="!isTierLocked(index)">
                                                <h4
                                                  class="summary-progress-text tx-font-primary text-center tx-gray-dark"
                                                  :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-progress`"
                                                >
                                                  {{ currency(tier.pointsAcquired) }} / {{ currency(getNextTierThreshold(index)) }}
                                                </h4>
                                                <v-progress-linear
                                                    class="progress-bar"
                                                    color="rgb( 2,178,223 )"
                                                    background-color="rgba( 229,229,229 )"
                                                    height="6"
                                                    :value="tier.pointsAcquired!=0?(tier.pointsAcquired/getNextTierThreshold(index)*100):0"
                                                    :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-progress-linear`"
                                                />
                                                <v-card-actions
                                                  class="summary-actions mt-8 pa-0"
                                                  :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-actions-container`"
                                                >
                                                    <a
                                                      class="link"
                                                      :href="mapLink(tier.shopNowLink)"
                                                      @click="publishEvent($event, tier)"
                                                      :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-shop-now-link`"
                                                    >
                                                        <button
                                                          type="button"
                                                          class="primary-solid fluid text-center"
                                                          :id="`${baseId}-tiering-other-content-tier-${index}-tier-unlock-shop-now-button`"
                                                        >
                                                          {{ $nsLanguage.getTranslation('tieringDashboardPage.' + tier.shopNowText) }}
                                                        </button>
                                                    </a>
                                                </v-card-actions>
                                            </template>
                                        </template>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </v-card>
                </v-flex>
            </v-layout>
        </template>
    </v-container>
</template>

<script>

import { formatPoints } from '../../../services/points'

import { events } from '@nuskin/ns-util'

export default {
    name: 'DashboardTiering',
    props: {
        showLocked: {
            type: Boolean,
            default: true
        },
        links: {
            type: Object,
            default: function () {
                return {}
            }
        },
        parentId: {
            type: String,
            required: false,
            default: ''
        }
    },
    data () {
        return {
            tiers: [],
            activeTierId: '',
            activeTierIndex: -1,
            visibleTierIndex: 0
        }
    },
    computed: {
        baseId: function () {
            const defaultBase = 'dashboard-white-tiering-cards'
            if (this.parentId) {
                return `${this.parentId}-${defaultBase}`
            } else {
                return defaultBase
            }
        },
        layout () {
            let layout

            if (this.tiers.length < 1) {
                layout = 'none'
            } else if (this.tiers.length > 0 && this.tiers[0].template === 'custom') {
                layout = 'custom'
            } else if (this.isMobile || this.isTablet) {
                layout = 'single'
            } else {
                layout = 'horizontal'
            }

            return layout
        },
        tierWidth () {
            if (this.layout === 'horizontal') {
                return Math.floor(100 / this.tiers.length).toFixed(4) + '%'
            }

            return '100%'
        },
        isMobile () {
            return this.$vuetify.breakpoint.smAndDown
        },
        isTablet () {
            return this.$vuetify.breakpoint.md
        }
    },
    methods: {
        currency (value) {
            return formatPoints(value)
        },
        getFocusDirection () {
            return this.activeTierIndex < (this.tiers.length * 0.50) ? 1 : -1
        },
        getHighlightType (index) {
            return (index === this.activeTierIndex ? 'all' : 'none')
        },
        getLayerIndex (index) {
            return this.getFocusDirection() > 0 ? (this.tiers.length - index) : (index + 1)
        },
        getNextTierSpendWithinPeriod (index) {
            const nextTier = this.tiers[(index + 1)]
            if (nextTier) {
                return nextTier.spendWithinPeriodDays
            }
        },
        getNextTierThreshold (index) {
            const nextTier = this.tiers[(index + 1)]
            if (nextTier) {
                return nextTier.tierThreshold
            }
        },
        getTierHighlightColor (index) {
            return this.isTierActive(index) ? 'blue' : 'gray'
        },
        getTierName (index) {
            const tier = this.tiers[index]
            return tier ? this.$nsLanguage.getTranslation('tieringDashboardPage.' + tier.tierId) : ''
        },
        isTierActive (index) {
            return index === this.activeTierIndex
        },
        isTierLocked (index) {
            return index > this.activeTierIndex
        },
        isTierUnlockable (index) {
            const tier = this.tiers[index]
            return tier ? tier.unlockable : false
        },
        isTierVisible (index) {
            return this.isMobile || this.isTablet ? (index === this.visibleTierIndex) : true
        },
        mapLink (link) {
            return this.links[link]
        },
        setActiveTier (tierId) {
            this.activeTierId = tierId

            const updateMenu = this.activeTierIndex < 0

            this.activeTierIndex = this.visibleTierIndex = this.tiers.findIndex(function (tier) {
                return tierId === tier.tierId
            })

            if (updateMenu) {
                this.visibleTierIndex = this.activeTierIndex
            }
        },
        setTiers (tiers) {
            this.tiers = tiers
        },
        showTierUnlockContent (tier, index) {
            return this.showLocked && this.isTierUnlockable(index + 1) && this.isTierLocked(index + 1) && this.isTierActive(index)
        },
        switchTab (eventName) {
            events.tag(eventName, { context: 'dashboard' })
        },
        publishEvent ($event, tier) {
            if (tier.event) {
                events.tag(tier.event, { context: 'dashboard' })
            }
        }
    }
}

</script>

<style lang="scss" scoped>

@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/buttons";
@use "~@nuskin/ns-core-styles/src/scss/colors";

$color-blue: #02B2DF;
$color-gray-38: map.get(colors.$baseColors, blackhole);
$color-gray-102: map.get(colors.$baseColors, dryink);
$color-gray-207: map.get(colors.$baseColors, steel);
$color-white: #FFFFFF;

$tier-active-offset-horizontal: 16px;
$tier-active-offset-vertical: 44px;
$tier-active-padding: 25px;
$tier-active-scale: 1.07;

.tx-em-bold-700 {
    ::v-deep {
        b, em, i {
            font-family: inherit;
            font-style: inherit;
            font-weight: 700;
        }
    }
}

.tiering
{
    padding: 0;

    ::v-deep {
        .v-progress-linear__determinate {
            position: relative;
        }
    }

    .tiering-menu {
        display: none;
    }

    &.layout-single {
        .tiering-menu {
            display: block;
        }

        .tier-container {
            padding: 0;
        }

        .tiering-content {
            flex-direction: column;
        }

        .tier-summary {
            margin: 0;
            padding: 0;
            min-height: 300px;
        }

        .tier-title {
            display: none;
        }

        .tier-visible {
            display: block;
        }
    }

    &.layout-horizontal {
        .tier-card {
            border-radius: 20px !important;
            box-shadow: 0 2px 17px 3px rgba(132,129,129,0.25);
        }

        .tiering-content {
            flex-direction: row;
            padding: 20px;
        }

        .tier-summary {
            display: inline-block;
            margin: 1px;
            min-height: 450px;
        }

        @for $ii from 1 through 8 {
            .tier-layer-#{$ii} {
                .tier-card {
                    z-index: ($ii * 10) !important;
                }
            }
        }

        .tier-active {
            .tier-card {
                z-index: 100 !important;
                top: -$tier-active-offset-vertical;
                bottom: -$tier-active-offset-vertical;
                left: -$tier-active-offset-horizontal;
                right: -$tier-active-offset-horizontal;
            }

            .tier-container {
                transform:
                    scale($tier-active-scale)
                    translate(0, -$tier-active-padding);
            }
        }
    }

    &.layout-custom {
        .tiering-content {
            flex-direction: column;
        }

        .tier-summary {
            display: block;
        }
    }
}

.tiering-menu {
    ::v-deep {
        .v-tabs-slider-wrapper {
            height: 3px;

            .v-tabs-slider {
                $transition-duration: 300ms;
                $transition-function: cubic-bezier(0.25, 0.80, 0.50, 1);

                transition: background-color $transition-duration $transition-function, border-color $transition-duration $transition-function;
            }
        }
    }

    &.tiering-menu-color-blue {
        ::v-deep {
            .v-tabs-slider {
                background-color: $color-blue !important;
                border-color: $color-blue !important;
            }
        }
    }

    &.tiering-menu-color-gray {
        ::v-deep {
            .v-tabs-slider {
                background-color: rgba( 0,0,0,0.50 ) !important;
                border-color: rgba( 0,0,0,0.50 ) !important;
            }
        }
    }

    .v-tabs-bar {
        background-color: transparent;
    }

    .tab-image {
        width: 16px;
        height: 16px;
        margin-bottom: 10px;
    }

    .tier-tab-container {
        display: block;
    }

    .tab-text {
        color: rgba( 0,0,0,0.50 );
        font-size: 20px;
        font-weight: 600;
    }

    .tab-active {
        .tab-text {
            color: rgb( 2,178,223 );
        }
    }
}

.tiering-content {
    display: flex;
    flex-wrap: nowrap;
    padding: 0;
}

.tier-summary {
    display: none;
    overflow: visible;
    position: relative;
    text-align: initial;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    background-color: transparent;
    border-radius: 0;
    box-shadow: none;
    font-size: 14px;

    ::v-deep .v-progress-linear__determinate {
        position: relative;
    }

    .tier-card {
        z-index: 1;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: $color-white;
        box-shadow: none;
    }

    .tier-container {
        z-index: 200;
        position: relative;
        padding: $tier-active-padding;
    }

    .custom-message {
        ::v-deep {
            em {
                font-style: normal;
                font-weight: 800;
            }
        }
    }

    .summary-bullet-list {
        &,
        & li {
            list-style-type: none;
            list-style-image: none;
            list-style-position: inside;
            margin: 0;
            padding: 0;
        }

        li {
            display: flex;
            align-content: center;
            align-items: center;
            position: relative;
            margin-top: 15px;

            ::v-deep em {
                font-style: normal;
                font-weight: 700;
            }

            .text {
                font-weight: normal;
                line-height: 1.11;
            }

            &:first-child {
                margin-top: 0;
            }
        }

        li.checkmark {
            min-height: 29px;
            padding-left: 42px;

            &::before {
                display: inline-block;
                position: absolute;
                top: 0;
                left: 0;
                width: 29px;
                height: 29px;
                background-image: url( '~@/assets/icons/Icon_Checkmark_Inactive.svg' );
                background-attachment: local;
                background-origin: padding-box;
                background-position: center center;
                background-repeat: no-repeat;
                background-size: contain;
                content: ' ';
            }
        }
    }

    .summary-actions {
        width: 100%;

        .link {
            display: block;
            width: 100%;

            button {
                padding: 8px;
            }
        }
    }

    .summary-divider {
        border-color: $color-gray-207;
    }

    .summary-number {
        line-height: 1;
        color: rgba( 0,0,0,0.50 );
        font-size: 45px;
        font-weight: 600;
    }

    .summary-message {
        font-weight: normal;
        line-height: 1.11;
    }

    .summary-instruction {
        font-size: 12px;
        font-weight: normal;
    }

    .summary-link {
        font-size: 12px;
        font-weight: normal;
        text-decoration: underline;
        color: #00b5e2;
    }

    .summary-progress-text {
        margin-bottom: 8px;
        font-size: 1em;
        font-weight: normal;
        letter-spacing: 0px;
    }

    .summary-section {
        z-index: 10;
        line-height: 1.22;
        margin: 32px 0;
        font-weight: normal;
        border-radius: 0;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .summary-section-heading {
        margin-bottom: 12px;
        padding: 0;
        font-size: inherit;
        font-weight: 700;
        line-height: 1.11;
        text-transform: uppercase;
    }

    .summary-watermark {
        z-index: 1;
        position: absolute;
        bottom: 20px;
        left: 0;
        right: 0;
    }

    .unlock-content {
        position: relative;
        z-index: 10;
    }

    .tier-title {
        margin: 0 0 15px;
        padding: 0;

        .text {
            font-size: 20px !important;
            font-weight: 600 !important;
            text-transform: uppercase;
        }
    }

    .tx-gray-light {
        color: $color-gray-102;
    }

    .tx-gray-dark {
        color: $color-gray-38;
    }

    .watermark {
        max-width: 50%;
        margin: 0 auto;
    }

    &.tier-em-all {
        .summary-bullet-list {
            li.checkmark {
                &::before {
                    background-image: url( '~@/assets/icons/Icon_Checkmark_Active.svg' );
                }
            }
        }

        .summary-divider.hightlight {
            border-color: $color-blue;
        }

        .summary-number {
            color: $color-blue;
        }

        .tier-title {
            .text {
                color: $color-blue;
            }
        }
    }

    &.tier-em-basic {
        .summary-bullet-list {
            li.checkmark {
                &::before {
                    background-image: url( '~@/assets/icons/Icon_Checkmark_Active.svg' );
                }
            }
        }

        .summary-divider.hightlight {
            border-color: $color-blue;
        }

        .summary-number {
            color: $color-blue;
        }
    }
}

</style>
